import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ensureProLinked } from '@/lib/proSelfHeal';
import { useSession } from '@/lib/auth';
import { LocationProvider } from '@/lib/locationContext';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Layout from '@/components/Layout';
import ConsentGate from '@/components/ConsentGate';
import Home from '@/pages/Home';
import MyInfo from '@/pages/MyInfo';
import MyStudio from '@/pages/MyStudio';
import Grow from '@/pages/Grow';
import Reviews from '@/pages/Reviews';
import CheckMyListing from '@/pages/CheckMyListing';
import GetOnTv from '@/pages/GetOnTv';
import Drawings from '@/pages/Drawings';
import Spotlight from '@/pages/Spotlight';
import CoiUpload from '@/pages/CoiUpload';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Support from '@/pages/Support';
import Help from '@/pages/Help';
import HelpArticle from '@/pages/HelpArticle';
import BuildStory from '@/pages/BuildStory';
import AdminMessages from '@/pages/admin/Messages';
import AdminSequences from '@/pages/admin/Sequences';
import AdminQAPortal from '@/pages/admin/QAPortal';
import { isCell } from '@/lib/cell/config';

/** /academy -> My Studio's Academy tab, keeping ?s=<class> (the studio's reminder text links here; ACADEMY-1). */
function AcademyRedirect() {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  p.set('tab', 'academy');
  return <Navigate to={`/my-studio?${p.toString()}`} replace />;
}

/** CELL (M28): the operator tools are not part of the AWS Pro Hub. Messages/Sequences send to real people and QA
 *  reads brain tables that are not on the cell; on AWS they belong to the Owner Hub. Nothing on these routes calls anything on the cell build. */
function NotOnCell() {
  return <div className="p-8 text-sm text-brand-slate">This operator tool is not part of the Pro Hub on Tenean&rsquo;s AWS build. Use the Owner Hub.</div>;
}

export default function App() {
  const { session, loading } = useSession();

  // THE PRO SELF-HEAL GATE (see lib/proSelfHeal.ts). Pages query pros by
  // auth_user_id the moment they mount, so an unlinked pro's first-ever
  // session must link BEFORE the first page renders — a heal racing those
  // queries shows exactly the empty hub it exists to prevent. Fail-open:
  // healed flips true whatever the heal's outcome, so nothing can wedge
  // the splash. Costs one indexed head-count per browser session
  // (sessionStorage-flagged after that).
  const [healedFor, setHealedFor] = useState<string | null>(null);
  const userId = session?.user?.id ?? null;
  useEffect(() => {
    if (loading) return;
    if (!userId) { setHealedFor(null); return; }
    let cancelled = false;
    ensureProLinked(userId).finally(() => { if (!cancelled) setHealedFor(userId); });
    return () => { cancelled = true; };
  }, [loading, userId]);
  const healing = !!userId && healedFor !== userId;

  // Neutral splash so we don't flash login for users with an existing
  // session. Plain background, no chrome.
  if (loading || healing) {
    return (
      <div className="min-h-screen bg-brand-fog grid place-items-center">
        <div className="text-brand-slate text-sm">Loading…</div>
      </div>
    );
  }

  // Auth callback runs in its own route — it owns the URL during the
  // PKCE exchange. Login redirect logic shouldn't preempt it.
  //
  // /privacy and /terms are PUBLIC. They live above the session
  // check so Apple App Review, Google's OAuth consent screen, and
  // visitors arriving from the App Store listing can read them
  // without signing in. Required by Apple Guideline 5.1.1 and
  // Google's OAuth consent screen URL fields.
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      {/* Public Support page — declared as the App Store Support URL.
          Apple Guideline 1.5 requires this URL respond with current,
          helpful support info accessible without auth. */}
      {/* These pages arrived as verbatim copies of support-web's PKNT Ops
          console pages — wrong product name on the pro surface. Kept PUBLIC
          (the comment above records an App Store dependency on /support
          answering without auth) and rebranded to IMAGE OS, 2026-08-26. */}
      <Route path="/support" element={<Support />} />
      {/* CustomerHelp renderer — public, no auth gate. /help is the
          index; /help/<surface>/<slug> renders an individual article
          from public.customer_help_articles (WI-113). */}
      <Route path="/help" element={<Help />} />
      <Route path="/help/*" element={<HelpArticle />} />
      {/* Public retrospective — same shape as Cadence's build-story.
          Visible to anyone with the link; lives outside the auth gate
          so an IMAGE Corporate exec can read it without signing in. */}
      <Route path="/build-story" element={<BuildStory />} />
      {!session ? (
        <>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      ) : (
        <Route
          element={
            // The consent gate wraps EVERYTHING a signed-in pro can reach —
            // first sign-in (or a terms-version bump) shows the agreement
            // interstitial before any app content renders.
            <ConsentGate session={session}>
              <LocationProvider>
                <Layout />
              </LocationProvider>
            </ConsentGate>
          }
        >
          <Route path="/" element={<Home />} />
          <Route path="/my-info" element={<MyInfo />} />
          {/* The facility wing (Two Wings IA, decision aa4fc89d). */}
          <Route path="/my-studio" element={<MyStudio />} />
          {/* Gated by the 'grow' switchboard key — the page itself carries the fence. */}
          <Route path="/grow" element={<Grow />} />
          {/* Gated by the 'reviews' switchboard key — the page itself carries the fence. */}
          <Route path="/reviews" element={<Reviews />} />
          {/* CHECK MY LISTING — the address the approved design names in its own breadcrumb
              ("Pro Hub › Grow › Check my listing"). Its home is the Grow tab; this direct route
              exists so the page can be linked to and bookmarked. */}
          <Route path="/check-my-listing" element={<CheckMyListing />} />
          {/* GET ON THE TV (TV-PRO-1) — gated by the 'tv' switchboard key; the page itself carries the fence. */}
          <Route path="/tv" element={<GetOnTv />} />
          {/* YOUR DRAWING (PRO-DRAW-1): the pro's own drawing and who signed up, behind the 'drawings' switch. */}
          <Route path="/drawings" element={<Drawings />} />
          {/* The Signature lives as a Grow wing tab (Peter's IA call, 2026-08-29); the old
              address keeps working for anything that linked it. */}
          <Route path="/signature" element={<Navigate to="/grow?tab=signature" replace />} />
          <Route path="/tickets" element={<Navigate to="/my-studio?tab=repairs" replace />} />
          {/* /tickets/list retired — past tickets live on /my-info now */}
          <Route path="/spotlight" element={<Spotlight />} />
          <Route path="/suggestions" element={<Navigate to="/my-studio?tab=ideas" replace />} />
          <Route path="/refer" element={<Navigate to="/my-studio?tab=refer" replace />} />
          <Route path="/academy" element={<AcademyRedirect />} />
          {/* /events RETIRED 2026-08-26 (3 rows ever, all on retired sharon_ma,
              no writer on any surface) — the address survives for old links. */}
          <Route path="/events" element={<Navigate to="/academy" replace />} />
          <Route path="/forms/coi" element={<CoiUpload />} />
          <Route path="/documents" element={<Navigate to="/my-studio?tab=docs" replace />} />
          <Route path="/admin/messages" element={isCell() ? <NotOnCell /> : <AdminMessages />} />
          <Route path="/admin/sequences" element={isCell() ? <NotOnCell /> : <AdminSequences />} />
          {/* /admin/stress RETIRED 2026-10-27 with its two functions (stress-fire, stress-cleanup; Cora's ruling,
              tenean-infra #864) — every button on the page called one of them. The address survives for old links. */}
          <Route path="/admin/stress" element={<Navigate to="/" replace />} />
          <Route path="/admin/qa" element={<AdminQAPortal />} />  {/* CELL (M28): the QA test loop is on AWS (Cora 2026-10-29) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
  );
}
